import { Component, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { BackendService, DialogService } from '@core/services';
import { BaseComponent, DialogRef } from '@shared/classes';
import { ButtonComponent } from '@shared/components/button/button.component';
import { CheckboxComponent } from '@shared/components/form-fields';
import { ChallengeBaseDTO } from '@shared/dto';
import {
  AnyChallenge,
  DIALOG_DATA,
  DialogCloseButton,
  DialogConfig,
  OptinsDialog,
} from '@shared/models';
import { JoinPipe, SafePipe } from '@shared/pipes';

import { CloseConfirmationModalComponent } from './close-confirmation-modal/close-confirmation-modal.component';

@Component({
  selector: 'b2c-optins-modal',
  imports: [
    SafePipe,
    JoinPipe,
    FormsModule,
    ButtonComponent,
    CheckboxComponent,
    ReactiveFormsModule,
  ],
  templateUrl: './optins-modal.component.html',
  styleUrl: './optins-modal.component.scss',
})
export class OptinsModalComponent extends BaseComponent implements OnInit {
  public config: OptinsDialog = this.loyaltyChallengeConfig.components.modals
    .optins as OptinsDialog;

  public closeButtonConfig: DialogCloseButton;

  private _optinsForm: FormGroup = new FormGroup({});

  public displayErrorMsg = signal(false);

  public selectionInProgress = signal(false);

  get optinsForm(): FormGroup {
    return this._optinsForm;
  }

  public data: AnyChallenge = inject<DialogConfig>(DIALOG_DATA)
    .data as AnyChallenge;

  private readonly router: Router = inject(Router);
  private readonly dialogRef: DialogRef = inject(DialogRef);
  private readonly dialog: DialogService = inject(DialogService);
  private readonly backendService: BackendService = inject(BackendService);

  ngOnInit(): void {
    this._optinsForm = new FormGroup({
      optin: new FormControl(false, [Validators.requiredTrue]),
    });
    const closeBtn: DialogCloseButton = this.loyaltyChallengeConfig.components
      .modals.optins?.actionButtons.closeButton as DialogCloseButton;
    this.closeButtonConfig =
      closeBtn ?? this.loyaltyChallengeConfig.components.modals.closeButton;
  }

  sendOptinAndSelectChallenge() {
    if (this.selectionInProgress()) {
      return;
    }
    if (this.optinsForm.invalid) {
      this.displayErrorMsg.set(true);
      return;
    }
    this.selectionInProgress.set(true);
    this.backendService
      .sendOptins()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.backendService
            .selectChallenge(this.data.campaignId, this.data.offerId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (data: ChallengeBaseDTO) => {
                this.dataService.currentChallenges.set(
                  this.dataService
                    .currentChallenges()
                    .map((c: AnyChallenge) => {
                      if (c.offerId === this.data.offerId) {
                        // Clone the class instance to preserve methods while creating new reference
                        const clone = Object.assign(
                          Object.create(Object.getPrototypeOf(c)),
                          c
                        );
                        clone.selectedDate = data.selected_date;
                        return clone;
                      }
                      return c;
                    })
                );
                this.closeDialog();
                this.selectionInProgress.set(false);
              },
            });
        },
        error: () => {
          console.error('an error happened');
          this.router.navigate(['login'], {
            queryParamsHandling: 'preserve',
          });
          this.closeDialog();
          this.selectionInProgress.set(false);
        },
      });
  }

  /**
   * Close DialogRef Reference
   */
  closeDialog(): void {
    this.dialogRef.close();
  }

  async showConfirmationDialog(): Promise<void> {
    const dialogRef: DialogRef = await this.dialog.open(
      CloseConfirmationModalComponent,
      undefined
    );

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: unknown) => {
          if (!result) {
            this.dialogRef.close();
          }
        },
        error: () => {
          console.error('error');
        },
      });
  }
}
